import { createPhantomTypeSchema, MaybeT, t } from '../../../../universal';
import { _, React } from '../../../lib';
import { Textbox } from '../textbox';
import { readDynamic } from './types';
const NumericWrapperT = t.Object({
    rawValue: t.Union([t.String(), t.Null()]),
    numeric: t.Union([t.Number(), t.Null()]),
    isValid: t.Union([t.Boolean(), t.Null()]),
});
export const FormTypeNumeric = (settings) => ({
    ...settings,
    valueDefaults: {
        def: () => null,
        validators: [
            {
                req: x => Boolean(x.isValid),
                msg: 'invalid number',
            },
            {
                req: x => {
                    const min = settings.min;
                    return x.numeric == null || min == null || x.numeric >= min;
                },
                msg: `min allowed: ${settings.min}`,
            },
            {
                req: x => {
                    const max = settings.max;
                    return x.numeric == null || max == null || x.numeric <= max;
                },
                msg: `max allowed: ${settings.max}`,
            },
        ],
        fixer: x => {
            let number = x.numeric;
            let isValid = x.isValid;
            if (!settings.disableKSuffix && x.rawValue?.endsWith('k')) {
                const num = Number(x.rawValue.slice(0, -1));
                if (!isNaN(num)) {
                    number = num * 1000;
                    isValid = true;
                }
            }
            if (!isValid) {
                return x;
            }
            const intl = new Intl.NumberFormat('en-AU', {
                minimumFractionDigits: settings.decimals ?? 0,
                maximumFractionDigits: settings.decimals ?? 0,
            });
            let s = number != null ? intl.format(number) : '';
            if (settings.disableThousandCommas) {
                s = s.replace(/,/g, '');
            }
            s = `${settings.prefix ?? ''}${s}${settings.suffix ?? ''}`;
            return {
                rawValue: s,
                numeric: number,
                isValid: true,
            };
        },
    },
    height: 36,
    typeMap: {
        schemaPublic: createPhantomTypeSchema(MaybeT(t.Number())),
        schemaRaw: createPhantomTypeSchema(NumericWrapperT),
        toPublic: x => x.numeric,
        toInternal: x => ({
            isValid: x == null || !isNaN(x),
            numeric: x == null || isNaN(x) ? null : x,
            rawValue: (x ?? '').toString(),
        }),
    },
    llmInfo: formState => ({
        stringifiedType: 'number',
        description: readDynamic(settings.doc, formState) ?? readDynamic(settings.lbl, formState),
        reversal: x => {
            const n = Number(x);
            return n;
        },
    }),
    jsx: props => (React.createElement(Textbox, { value: props.value.rawValue ?? '', onUpdate: v => {
            const num = extractRawNumberFromNumericString(settings, String(v)).parsedNumber;
            props.onUpdate({
                rawValue: v,
                numeric: isNaN(Number(num)) ? null : (num ?? null),
                isValid: !isNaN(Number(num)),
            });
        }, className: props.className ?? undefined, title: props.title ?? undefined, placeholder: settings.placeholder, readOnly: props.readOnly, disabled: props.disabled, onFocus: e => {
            const cStart = e.target.selectionStart;
            const cEnd = e.target.selectionEnd;
            const s = String(e.target.value);
            const num = extractRawNumberFromNumericString(settings, s);
            if (num.parsedNumber && !isNaN(num.parsedNumber)) {
                props.onUpdate({
                    rawValue: String(num.parsedNumber),
                    numeric: num.parsedNumber,
                    isValid: true,
                });
                requestAnimationFrame(() => {
                    if (document.activeElement === e.target) {
                        const ncStart = num.posMap[cStart ?? -1] ?? null;
                        const ncEnd = num.posMap[cEnd ?? -1] ?? null;
                        e.target.setSelectionRange(ncStart, ncEnd);
                    }
                });
            }
            props.onFocus?.();
        }, onBlur: props.onBlur, "data-1p-ignore": true })),
});
const extractRawNumberFromNumericString = (settings, str) => {
    let s = str;
    if (!s) {
        return {
            parsedNumber: null,
            posMap: {},
        };
    }
    const posMap = _.fromPairs(_.range(s.length + 1).map(i => [i, i]));
    if (settings.suffix && s.endsWith(settings.suffix)) {
        const cutoff = s.length - settings.suffix.length;
        s = s.slice(0, cutoff);
        _.forEach(posMap, (v, k) => {
            if (v >= cutoff) {
                posMap[k] = cutoff;
            }
        });
    }
    const prefix = settings.prefix;
    if (prefix && s.startsWith(prefix)) {
        s = s.slice(prefix.length);
        _.forEach(posMap, (v, k) => {
            posMap[k] = v < prefix.length ? 0 : v - prefix.length;
        });
    }
    _.forEach(s, (c, i) => {
        if (c === ',' || c === ' ') {
            _.forEach(posMap, (v, k) => {
                if (v > i) {
                    posMap[k] = v - 1;
                }
            });
        }
    });
    s = s.replace(/,/g, '');
    let firstNonZero = -1;
    _.forEach(s, (c, i) => {
        if (c !== '0' && firstNonZero === -1) {
            firstNonZero = i;
        }
    });
    if (firstNonZero > 0) {
        s = s.slice(firstNonZero);
        _.forEach(posMap, (v, k) => {
            posMap[k] = v - firstNonZero;
        });
    }
    return {
        parsedNumber: Number(s),
        posMap: posMap,
    };
};
//# sourceMappingURL=fields-numeric.js.map